import { useEffect, useState, useRef } from "react";
import { MagnifyingGlass, MapPin } from "@phosphor-icons/react";
import { api } from "../lib/api";

/**
 * Address autocomplete — backed by Google Places (New) via our backend
 * proxy at `/api/places/autocomplete` + `/api/places/details/{id}`.
 *
 * Why backend proxy?
 *   - Google Places API key stays server-side — never leaks to the browser.
 *   - We can swap providers (Nominatim fallback / Geoscape / HERE) without
 *     touching this component.
 *   - 24h server-side cache reduces billed sessions.
 *
 * Session token strategy
 *   Google bills cheaper when an entire "typing session" (multiple
 *   autocomplete calls + a single Place Details lookup) shares one session
 *   token. We generate one per mount, reset after the user picks a result.
 */

// Generate an opaque session token (16 hex chars is plenty for Google).
const newSessionToken = () =>
  (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`).replace(/-/g, "").slice(0, 16);

export default function AddressAutocomplete({ value, onChange, placeholder = "Start typing the customer's address…", testid }) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [searching, setSearching] = useState(false);
  // Session token persists across multiple keystrokes in ONE search session.
  // Reset after a result is picked so the next address typed is a fresh
  // billing session.
  const sessionRef = useRef(newSessionToken());
  const blurTimer = useRef(null);
  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    const v = (query || "").trim();
    if (v.length < 4) { setSuggestions([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        // Backend proxy: never expose the Google key to the browser. Soft
        // falls back to Nominatim if billing is off.
        const r = await api.get("/places/autocomplete", {
          params: { q: v, session_token: sessionRef.current },
        });
        if (cancelled) return;
        const preds = r.data?.predictions || [];
        // The backend returns {place_id, description}. We keep place_id
        // alongside so the next click can hit /places/details for the
        // structured address (street_number, route, locality, etc.).
        setSuggestions(preds.map((p) => ({
          place_id: p.place_id,
          full: p.description,
          main: p.description.split(",")[0] || p.description,
          sub: p.description.split(",").slice(1).join(",").trim(),
        })).filter((x) => x.full));
        setHighlight(0);
      } catch {
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  const choose = async (s) => {
    // For Google predictions, fetch the canonical formatted address via
    // Place Details — gives a cleaner output (e.g. "10 Russell St,
    // Toowoomba City QLD 4350, Australia") than the prediction text.
    // Nominatim's fallback predictions don't carry a place_id Google can
    // resolve, so we fall through to the prediction text directly.
    let resolved = s.full;
    if (s.place_id && s.place_id.startsWith("ChIJ")) {
      try {
        const r = await api.get(`/places/details/${s.place_id}`, {
          params: { session_token: sessionRef.current },
        });
        if (r.data?.formatted_address) {
          resolved = r.data.formatted_address;
        }
      } catch {
        // Soft-fail — keep the prediction text on any error.
      }
    }
    setQuery(resolved);
    onChange?.(resolved);
    setOpen(false);
    // Reset the session — next address typed starts a fresh billing
    // session as Google's docs recommend.
    sessionRef.current = newSessionToken();
  };

  return (
    <div className="relative">
      <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
      <input
        value={query}
        onChange={(e) => { setQuery(e.target.value); onChange?.(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 150); }}
        onKeyDown={(e) => {
          if (!open || !suggestions.length) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, suggestions.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
          else if (e.key === "Enter")    { e.preventDefault(); choose(suggestions[highlight]); }
          else if (e.key === "Escape")   { setOpen(false); }
        }}
        placeholder={placeholder}
        className="h-12 w-full pl-9 pr-3 rounded-xl border-2 border-[#E5E7EB] text-[14px] focus:border-[#1E5FEB] outline-none transition-colors"
        data-testid={testid}
        autoComplete="off"
      />
      {open && (suggestions.length > 0 || searching) && (
        <div
          className="absolute left-0 right-0 top-full mt-1 bg-white rounded-xl border border-[#E5E7EB] overflow-hidden z-50"
          style={{ boxShadow: "0 14px 32px rgba(30, 95, 235, 0.14)" }}
          data-testid={`${testid}-dropdown`}
          onMouseDown={() => clearTimeout(blurTimer.current)}
        >
          {searching && suggestions.length === 0 && (
            <div className="px-4 py-3 text-[12px] text-[#9CA3AF]">Searching…</div>
          )}
          {suggestions.map((s, i) => {
            const hi = i === highlight;
            return (
              <button
                key={`${s.full}-${i}`}
                type="button"
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={(e) => { e.preventDefault(); choose(s); }}
                className={`w-full text-left px-4 py-2.5 flex items-start gap-2.5 ${hi ? "bg-[#F3F4F6]" : "hover:bg-[#FAFAFA]"}`}
                data-testid={`${testid}-suggest-${i}`}
              >
                <MapPin size={14} weight="bold" className="mt-1 text-[#1E5FEB] flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-[13px] font-medium leading-tight">{s.main}</div>
                  <div className="text-[11px] text-[#9CA3AF] mt-0.5">{s.sub}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
